/**
 * SJ Quick Cart
 * - Auto-adds the free gift when a gift milestone is unlocked, removes it when the cart drops below.
 * - "Add +" buttons in the cart upsell.
 * Reads state rendered by snippets/sj-cart-rewards.liquid, and refreshes the drawer the same way
 * Horizon does (CartLinesUpdateEvent + section HTML), so the cart icon and drawer stay in sync.
 */
import { CartLinesUpdateEvent } from '@shopify/events';

if (!window.__sjQuickCartReady) {
  window.__sjQuickCartReady = true;

  const SECTION_ID = 'cart-drawer-section';
  const routes = () => window.Theme?.routes || {};
  const addUrl = () => routes().cart_add_url || '/cart/add.js';
  const changeUrl = () => `${(routes().cart_change_url || '/cart/change').replace(/\.js$/, '')}.js`;
  const cartJsUrl = () => `${routes().cart_url || '/cart'}.js`;
  const jsonHeaders = { 'Content-Type': 'application/json', Accept: 'application/json' };

  let busy = false;
  let pending = false;
  let attempts = 0;

  const readState = () => {
    const el = document.querySelector('#cart-drawer [data-sjqc-state]') || document.querySelector('[data-sjqc-state]');
    if (!el) return null;
    try { return JSON.parse(el.textContent); } catch (e) { return null; }
  };

  /** Builds the list of cart operations needed to make gifts match the rewards state. */
  const planOps = (state) => {
    const ops = [];
    if (!state) return ops;
    (state.orphans || []).forEach((key) => ops.push({ type: 'change', id: key, quantity: 0 }));
    if (!state.autoGift) return ops;
    (state.gifts || []).forEach((g) => {
      const lines = g.lines || [];
      if (g.qualified && g.variantId) {
        if (!lines.length) ops.push({ type: 'add', variantId: g.variantId, milestone: g.milestone });
        lines.forEach((l, i) => {
          if (i === 0 && l.quantity !== 1) ops.push({ type: 'change', id: l.key, quantity: 1 });
          if (i > 0) ops.push({ type: 'change', id: l.key, quantity: 0 });
        });
      } else {
        lines.forEach((l) => ops.push({ type: 'change', id: l.key, quantity: 0 }));
      }
    });
    return ops;
  };

  /** Runs a cart request and announces it so Horizon morphs the drawer + updates the cart icon. */
  const runAndAnnounce = async (op, source) => {
    const isAdd = op.type === 'add';
    const deferred = CartLinesUpdateEvent.createPromise();
    document.dispatchEvent(
      new CartLinesUpdateEvent(
        isAdd
          ? { action: 'add', context: 'cart', lines: [{ merchandiseId: String(op.variantId), quantity: op.quantity || 1 }], promise: deferred.promise }
          : { action: op.quantity === 0 ? 'remove' : 'update', context: 'cart', lines: [{ id: op.id, quantity: op.quantity }], promise: deferred.promise }
      )
    );

    try {
      const body = isAdd
        ? {
            items: [{ id: Number(op.variantId), quantity: op.quantity || 1, ...(op.milestone ? { properties: { _sj_gift: String(op.milestone) } } : {}) }],
            sections: SECTION_ID,
            sections_url: window.location.pathname,
          }
        : { id: op.id, quantity: op.quantity, sections: SECTION_ID, sections_url: window.location.pathname };

      const response = await fetch(isAdd ? addUrl() : changeUrl(), {
        method: 'POST',
        headers: jsonHeaders,
        body: JSON.stringify(body),
      }).then((r) => r.json());

      const ajaxCart = await fetch(cartJsUrl(), { headers: { Accept: 'application/json' } }).then((r) => r.json());
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
        detail: {
          items: ajaxCart.items,
          source,
          itemCount: op.quantity || 1,
          sections: response.sections,
          didError: Boolean(response.status),
        },
      });
      return !response.status;
    } catch (error) {
      deferred.reject(error);
      return false;
    }
  };

  const sync = async () => {
    if (busy) { pending = true; return; }
    const ops = planOps(readState());
    if (!ops.length) { attempts = 0; return; }
    if (attempts >= 3) return; // safety: never loop forever (e.g. gift out of stock)
    attempts += 1;
    busy = true;
    for (const op of ops) {
      // eslint-disable-next-line no-await-in-loop
      await runAndAnnounce(op, 'sj-quick-cart-gift');
    }
    busy = false;
    // The drawer re-renders with fresh state; check again once it settles.
    setTimeout(sync, 400);
    if (pending) { pending = false; setTimeout(sync, 450); }
  };

  // Re-check whenever the drawer HTML changes (Horizon morphs it after every cart change).
  let timer;
  const schedule = () => { clearTimeout(timer); timer = setTimeout(sync, 250); };
  const observer = new MutationObserver((records) => {
    if (records.some((r) => [...r.addedNodes, ...r.removedNodes].some((n) => n.nodeType === 1) || r.type === 'characterData')) schedule();
  });
  const startObserving = () => {
    const drawer = document.getElementById('cart-drawer') || document.body;
    observer.observe(drawer, { childList: true, subtree: true, characterData: true });
  };

  document.addEventListener('cart:update', () => { attempts = 0; schedule(); });
  document.addEventListener('click', (e) => {
    if (e.target.closest('#cart-drawer, cart-items-component')) attempts = 0;
  }, true);

  // Upsell "Add +"
  document.addEventListener('click', async (e) => {
    const btn = e.target.closest('[data-sjqc-add]');
    if (!btn) return;
    e.preventDefault();
    if (btn.getAttribute('aria-busy') === 'true') return;
    btn.setAttribute('aria-busy', 'true');
    attempts = 0;
    const ok = await runAndAnnounce({ type: 'add', variantId: btn.dataset.sjqcAdd, quantity: 1 }, 'sj-quick-cart-upsell');
    btn.removeAttribute('aria-busy');
    if (!ok) btn.textContent = 'Sold out';
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => { startObserving(); sync(); });
  } else {
    startObserving();
    sync();
  }
}
